<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useInventoryStore, type InventoryItem, type CreateItemRequest } from '../stores/inventory'
import InventoryCard from '../components/InventoryCard.vue'
import PhotoUploader from '../components/PhotoUploader.vue'

const authStore = useAuthStore()
const inventoryStore = useInventoryStore()
const router = useRouter()

// View state
const viewMode = ref<'grid' | 'list'>('grid')
const searchQuery = ref('')
const selectedLocation = ref('')
const selectedCategory = ref('')
const selectedCondition = ref('')

// Modal state
const showAddModal = ref(false)
const showDeleteConfirm = ref(false)
const itemToDelete = ref<InventoryItem | null>(null)
const editingItem = ref<InventoryItem | null>(null)

// Form state
const formData = ref<CreateItemRequest>({
  name: '',
  description: '',
  make: '',
  model: '',
  serial_number: '',
  purchase_location: '',
  purchase_date: '',
  purchase_price: undefined,
  warranty_expires: '',
  photo_key: '',
  location_id: '',
  category_id: '',
  condition: undefined,
  notes: '',
  manual_url: '',
})

const formError = ref('')
const saving = ref(false)

const filteredItems = computed(() => {
  let items = inventoryStore.items

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    items = items.filter(item =>
      item.name.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query) ||
      item.make?.toLowerCase().includes(query) ||
      item.model?.toLowerCase().includes(query)
    )
  }

  return items
})

onMounted(async () => {
  if (!authStore.canViewInventory) {
    router.push('/')
    return
  }
  await Promise.all([
    inventoryStore.fetchCategories(),
    inventoryStore.fetchItems(),
  ])
})

async function applyFilters() {
  await inventoryStore.fetchItems({
    location: selectedLocation.value || undefined,
    category: selectedCategory.value || undefined,
    condition: selectedCondition.value || undefined,
    search: searchQuery.value || undefined,
  })
}

function clearFilters() {
  searchQuery.value = ''
  selectedLocation.value = ''
  selectedCategory.value = ''
  selectedCondition.value = ''
  inventoryStore.fetchItems()
}

function openAddModal() {
  editingItem.value = null
  formData.value = {
    name: '',
    description: '',
    make: '',
    model: '',
    serial_number: '',
    purchase_location: '',
    purchase_date: '',
    purchase_price: undefined,
    warranty_expires: '',
    photo_key: '',
    location_id: '',
    category_id: '',
    condition: undefined,
    notes: '',
    manual_url: '',
  }
  formError.value = ''
  showAddModal.value = true
}

function openEditModal(item: InventoryItem) {
  editingItem.value = item
  formData.value = {
    name: item.name,
    description: item.description || '',
    make: item.make || '',
    model: item.model || '',
    serial_number: item.serial_number || '',
    purchase_location: item.purchase_location || '',
    purchase_date: item.purchase_date || '',
    purchase_price: item.purchase_price || undefined,
    warranty_expires: item.warranty_expires || '',
    photo_key: item.photo_key || '',
    location_id: item.location_id || '',
    category_id: item.category_id || '',
    condition: item.condition || undefined,
    notes: item.notes || '',
    manual_url: item.manual_url || '',
  }
  formError.value = ''
  showAddModal.value = true
}

function closeModal() {
  showAddModal.value = false
  editingItem.value = null
}

async function saveItem() {
  if (!formData.value.name) {
    formError.value = 'Name is required'
    return
  }

  saving.value = true
  formError.value = ''

  try {
    if (editingItem.value) {
      await inventoryStore.updateItem(editingItem.value.id, formData.value)
    } else {
      await inventoryStore.createItem(formData.value)
    }
    closeModal()
  } catch (err) {
    formError.value = err instanceof Error ? err.message : 'Failed to save item'
  } finally {
    saving.value = false
  }
}

function confirmDelete(item: InventoryItem) {
  itemToDelete.value = item
  showDeleteConfirm.value = true
}

async function deleteItem() {
  if (!itemToDelete.value) return

  try {
    await inventoryStore.deleteItem(itemToDelete.value.id)
    showDeleteConfirm.value = false
    itemToDelete.value = null
  } catch (err) {
    console.error('Failed to delete item:', err)
  }
}

function viewItem(item: InventoryItem) {
  router.push(`/inventory/${item.id}`)
}

function handlePhotoUploaded(key: string) {
  formData.value.photo_key = key
}

function handlePhotoRemoved() {
  formData.value.photo_key = ''
}

function handlePhotoError(message: string) {
  formError.value = message
}

const API_BASE = import.meta.env.VITE_API_URL || ''
const currentPhotoUrl = computed(() => {
  if (formData.value.photo_key) {
    return `/api/inventory/photo/${formData.value.photo_key}`
  }
  return editingItem.value?.photo_url || null
})
</script>

<template>
  <div class="max-w-7xl mx-auto p-4 md:p-6">
    <!-- Header -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-800">House Inventory</h1>
        <p class="text-gray-600">Track appliances, furniture, and equipment</p>
      </div>
      <div class="flex gap-3">
        <button
          v-if="authStore.canManageInventory"
          @click="openAddModal"
          class="btn btn-primary"
        >
          + Add Item
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card mb-6">
      <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search items..."
          class="input"
          @keyup.enter="applyFilters"
        />
        <select v-model="selectedLocation" class="input">
          <option value="">All Locations</option>
          <option
            v-for="loc in inventoryStore.locations"
            :key="loc.id"
            :value="loc.id"
          >
            {{ loc.name }}
          </option>
        </select>
        <select v-model="selectedCategory" class="input">
          <option value="">All Categories</option>
          <option
            v-for="cat in inventoryStore.itemTypes"
            :key="cat.id"
            :value="cat.id"
          >
            {{ cat.name }}
          </option>
        </select>
        <select v-model="selectedCondition" class="input">
          <option value="">All Conditions</option>
          <option value="excellent">Excellent</option>
          <option value="good">Good</option>
          <option value="fair">Fair</option>
          <option value="poor">Poor</option>
          <option value="needs_replacement">Needs Replacement</option>
        </select>
        <div class="flex gap-2">
          <button @click="applyFilters" class="btn btn-primary flex-1">
            Search
          </button>
          <button @click="clearFilters" class="btn">
            Clear
          </button>
        </div>
      </div>
    </div>

    <!-- View Toggle -->
    <div class="flex justify-between items-center mb-4">
      <p class="text-gray-600">
        {{ filteredItems.length }} item{{ filteredItems.length !== 1 ? 's' : '' }}
      </p>
      <div class="flex gap-2">
        <button
          @click="viewMode = 'grid'"
          :class="['p-2 rounded', viewMode === 'grid' ? 'bg-primary-100 text-primary-700' : 'text-gray-500']"
          title="Grid view"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
        </button>
        <button
          @click="viewMode = 'list'"
          :class="['p-2 rounded', viewMode === 'list' ? 'bg-primary-100 text-primary-700' : 'text-gray-500']"
          title="List view"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="inventoryStore.loading" class="text-center py-12">
      <p class="text-gray-500">Loading inventory...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredItems.length === 0" class="text-center py-12">
      <p class="text-gray-500 mb-4">No items found</p>
      <button
        v-if="authStore.canManageInventory"
        @click="openAddModal"
        class="btn btn-primary"
      >
        Add First Item
      </button>
    </div>

    <!-- Items Grid -->
    <div
      v-else-if="viewMode === 'grid'"
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
    >
      <InventoryCard
        v-for="item in filteredItems"
        :key="item.id"
        :item="item"
        :can-edit="authStore.canManageInventory"
        @click="viewItem(item)"
        @edit="openEditModal(item)"
        @delete="confirmDelete(item)"
      />
    </div>

    <!-- Items List -->
    <div v-else class="space-y-2">
      <div
        v-for="item in filteredItems"
        :key="item.id"
        class="card flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow"
        @click="viewItem(item)"
      >
        <div class="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
          <img
            v-if="item.photo_url"
            :src="`${API_BASE}${item.photo_url}`"
            :alt="item.name"
            class="w-full h-full object-cover"
          />
        </div>
        <div class="flex-1 min-w-0">
          <h3 class="font-semibold text-gray-800 truncate">{{ item.name }}</h3>
          <p class="text-sm text-gray-500">
            {{ [item.location_name, item.category_name].filter(Boolean).join(' - ') }}
          </p>
        </div>
        <div
          v-if="authStore.canManageInventory"
          class="flex gap-2"
          @click.stop
        >
          <button @click="openEditModal(item)" class="btn btn-sm">Edit</button>
          <button @click="confirmDelete(item)" class="btn btn-sm btn-danger">Delete</button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div
      v-if="showAddModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto"
      @click.self="closeModal"
    >
      <div class="card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-start mb-4">
          <h2 class="text-xl font-bold text-gray-800">
            {{ editingItem ? 'Edit Item' : 'Add New Item' }}
          </h2>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>

        <div v-if="formError" class="bg-red-50 text-red-600 p-3 rounded-lg mb-4">
          {{ formError }}
        </div>

        <form @submit.prevent="saveItem" class="space-y-4">
          <!-- Photo -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Photo</label>
            <PhotoUploader
              :current-photo-url="currentPhotoUrl"
              upload-endpoint="inventory"
              @uploaded="handlePhotoUploaded"
              @removed="handlePhotoRemoved"
              @error="handlePhotoError"
            />
          </div>

          <!-- Basic Info -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input v-model="formData.name" type="text" class="input" required />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Condition</label>
              <select v-model="formData.condition" class="input">
                <option value="">Select condition</option>
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
                <option value="needs_replacement">Needs Replacement</option>
              </select>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <select v-model="formData.location_id" class="input">
                <option value="">Select location</option>
                <option
                  v-for="loc in inventoryStore.locations"
                  :key="loc.id"
                  :value="loc.id"
                >
                  {{ loc.name }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select v-model="formData.category_id" class="input">
                <option value="">Select category</option>
                <option
                  v-for="cat in inventoryStore.itemTypes"
                  :key="cat.id"
                  :value="cat.id"
                >
                  {{ cat.name }}
                </option>
              </select>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea v-model="formData.description" class="input" rows="2"></textarea>
          </div>

          <!-- Product Details -->
          <div class="border-t pt-4">
            <h3 class="font-medium text-gray-700 mb-3">Product Details</h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Make</label>
                <input v-model="formData.make" type="text" class="input" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Model</label>
                <input v-model="formData.model" type="text" class="input" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                <input v-model="formData.serial_number" type="text" class="input" />
              </div>
            </div>
          </div>

          <!-- Purchase Info -->
          <div class="border-t pt-4">
            <h3 class="font-medium text-gray-700 mb-3">Purchase Information</h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Purchase Location</label>
                <input v-model="formData.purchase_location" type="text" class="input" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Purchase Date</label>
                <input v-model="formData.purchase_date" type="date" class="input" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Price</label>
                <input v-model.number="formData.purchase_price" type="number" step="0.01" class="input" />
              </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Warranty Expires</label>
                <input v-model="formData.warranty_expires" type="date" class="input" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Manual URL</label>
                <input v-model="formData.manual_url" type="url" class="input" placeholder="https://..." />
              </div>
            </div>
          </div>

          <!-- Notes -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea v-model="formData.notes" class="input" rows="2"></textarea>
          </div>

          <!-- Actions -->
          <div class="flex gap-3 pt-4 border-t">
            <button type="button" @click="closeModal" class="btn flex-1">
              Cancel
            </button>
            <button
              type="submit"
              class="btn btn-primary flex-1"
              :disabled="saving"
            >
              {{ saving ? 'Saving...' : (editingItem ? 'Save Changes' : 'Add Item') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteConfirm"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      @click.self="showDeleteConfirm = false"
    >
      <div class="card max-w-md w-full">
        <h2 class="text-xl font-bold text-gray-800 mb-4">Delete Item?</h2>
        <p class="text-gray-600 mb-6">
          Are you sure you want to delete "{{ itemToDelete?.name }}"? This cannot be undone.
        </p>
        <div class="flex gap-3">
          <button @click="showDeleteConfirm = false" class="btn flex-1">
            Cancel
          </button>
          <button @click="deleteItem" class="btn btn-danger flex-1">
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
